/* eslint-disable max-depth */
/* eslint-disable react-func/max-lines-per-function */
/* eslint-disable max-lines */
import { saveAs } from 'file-saver';
import { max } from 'lodash-es';
import { ExcelCommentRow, Comment, CommentSourceType } from '@/types';
import { getCheckShowTree, parseCheckResultJSON } from './checkResultHandle';

type Workbook = any;
type Worksheet = any;
type Row = any;

const ABUNDANT = 50;
export const DEFAULT_COLUMN_WIDTH = 20;
export const DEFAULT_ROW_HEIGHT = 32;
const PREVIEW_COL_WIDTH = 500;
const PREVIEW_COL_HEIGHT = 250;
// 换算单元格的字符单位与图片的像素单位
const PREVIEW_COL_WIDTH_PIXEL = (PREVIEW_COL_WIDTH / 400) * 641;
// 富文本的图片占据的行数
const ROW_HEIGHT = 17;
const MAX_ROW_HEIGHT = ROW_HEIGHT * 22; // excel行高限制，超出需要合并单元格
const IMG_ROW_COUNTS = 10;
const IMG_HEIGHT = ROW_HEIGHT * IMG_ROW_COUNTS - 40;
const ROW_HEIGHT_PIXEL = 60 / 50;

const getCheckResultContent = (checkList) => {
  const files = getCheckShowTree(checkList)?.map(({ fileName, rules, group }, index) => {
    const rulesText = rules
      ?.map(({ tip }) => tip)
      ?.reduce(
        (acc, cur, curInd) => `${acc}
    ${index + 1}.${curInd + 1}、${cur}`,
        '',
      );
    const groupText = !group?.length
      ? ''
      : group?.reduce((acc, { name, rules: groupRules }, curInd) => {
          const groupRulesText = groupRules
            ?.map(({ tip }) => tip)
            ?.reduce(
              (acc, cur, curInd) => `${acc}
        ${String.fromCharCode(curInd + 97)}、${cur}`,
              '',
            );
          return `${acc}
    ${index + 1}.${rules?.length + curInd + 1}、${name} (Group) ${groupRulesText}`;
        }, '');
    return `${index + 1}、 ${fileName}${rulesText}${groupText}`;
  });
  const filesStr = files?.reduce(
    (acc, cur) => `${acc}

  ${cur}`,
    '',
  );
  return filesStr;
};
export const handleExcelData = (comments: Comment[]): ExcelCommentRow[] => {
  return comments?.map(
    ({ attachment, type, creator, annotation, content, isFullScreen, preview, popo, assetPosition, ...rest }) => {
      const isCheckResult = type === CommentSourceType.CheckResult;
      let assetPositionTip;

      if (type === CommentSourceType.Animation) {
        assetPositionTip = !!assetPosition.frameTime
          ? `${assetPosition.frameTime}\n${assetPosition?.snapShot?.params?.animationName}-${assetPosition?.frameIndex}帧`
          : `${assetPosition?.snapShot?.params?.animationName}-${assetPosition?.frameIndex}帧`;
      }
      if (type === CommentSourceType.Video) {
        assetPositionTip = !!assetPosition.frameTime
          ? `${assetPosition.frameTime}\n${assetPosition?.frameIndex}帧`
          : `${assetPosition?.frameIndex}帧`;
      }
      const fileName = assetPosition?.fileName;
      if (!isCheckResult) {
        return {
          content,
          hasAttachment: !!attachment?.length ? '是' : '否',
          creatorName: creator?.nickName,
          fileName,
          assetPositionTip,
          ...rest,
        };
      }
      const { checkList, errorInfo } = parseCheckResultJSON(content);
      const checkResultContent = errorInfo && !checkList?.length ? '' : getCheckResultContent(checkList);
      return {
        content: checkResultContent,
        hasAttachment: !!attachment?.length ? '是' : '否',
        creatorName: creator?.name,
        ...rest,
      };
    },
  );
};

export interface ITableHeader {
  header: string;
  key: string;
  width: number;
  // 嵌套表格父级的 key
  parentKey?: string;
  children?: ITableHeader[];
}

export interface IStyleAttr {
  color?: string;
  fontSize?: number;
  horizontal?: 'fill' | 'distributed' | 'justify' | 'center' | 'left' | 'right' | 'centerContinuous' | undefined;
  bold?: boolean;
}

export function saveWorkbook(workbook: Workbook, fileName: string) {
  workbook.xlsx.writeBuffer().then((data) => {
    const blob = new Blob([data], { type: '' });
    saveAs(blob, fileName);
  });
}

// 根据 antd 的 column 生成 exceljs 的 column
export function generateHeaders(columns: any[]) {
  return columns?.map((col) => {
    const obj: ITableHeader = {
      header: col.title,
      key: col.dataIndex,
      width: col.width / 5 || DEFAULT_COLUMN_WIDTH,
    };
    if (col.children) {
      obj.children = col.children?.map((item: any) => ({
        key: item.dataIndex,
        header: item.title,
        width: item.width,
        parentKey: col.dataIndex,
      }));
    }
    return obj;
  });
}

export function getColumnNumber(width: number) {
  return Math.round(width / DEFAULT_COLUMN_WIDTH);
}

export function addHeaderStyle(row: Row, attr?: IStyleAttr) {
  const { color, fontSize, horizontal, bold } = attr || {};
  row.height = DEFAULT_ROW_HEIGHT;
  row.eachCell((cell, colNumber) => {
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: color },
    };
    cell.font = {
      bold: bold ?? true,
      size: fontSize ?? 11,
      name: '微软雅黑',
    };
    cell.alignment = { vertical: 'middle', wrapText: true, horizontal: horizontal ?? 'left' };
  });
}

export function mergeRows(worksheet, startRowIndex, endRowIndex) {
  const columnCount = worksheet.columns.length;
  for (let i = 1; i <= columnCount; i++) {
    const charCodeA = 'A'.charCodeAt(0);
    const letter = String.fromCharCode(charCodeA + i - 1);
    worksheet.mergeCells(`${letter}${startRowIndex}:${letter}${endRowIndex}`);
  }
}

// 合并行和列，用于处理表头合并
export function mergeColumnCell(
  headers: ITableHeader[],
  rowHeader1: Row,
  rowHeader2: Row,
  nameRow1: string[],
  nameRow2: string[],
  worksheet: Worksheet,
) {
  // 当前 index 的指针
  let pointer = -1;
  nameRow1.forEach((name, index) => {
    // 当 index 小于指针时，说明这一列已经被合并过了，不能再合并
    if (index <= pointer) return;
    // 是否应该列合并
    const shouldVerticalMerge = name === nameRow2[index];
    // 是否应该行合并
    const shouldHorizontalMerge = index !== nameRow1.lastIndexOf(name);
    pointer = nameRow1.lastIndexOf(name);
    if (shouldVerticalMerge && shouldHorizontalMerge) {
      // 两个方向都合并
      worksheet.mergeCells(
        Number(rowHeader1.number),
        index + 1,
        Number(rowHeader2.number),
        nameRow1.lastIndexOf(name) + 1,
      );
    } else if (shouldVerticalMerge && !shouldHorizontalMerge) {
      // 只在垂直方向上同一列的两行合并
      worksheet.mergeCells(Number(rowHeader1.number), index + 1, Number(rowHeader2.number), index + 1);
    } else if (!shouldVerticalMerge && shouldHorizontalMerge) {
      // 只有水平方向同一行的多列合并
      worksheet.mergeCells(
        Number(rowHeader1.number),
        index + 1,
        Number(rowHeader1.number),
        nameRow1.lastIndexOf(name) + 1,
      );
      const cell = rowHeader1.getCell(index + 1);
      cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
    }
  });
}

// 行合并单元格
export function mergeRowCell(headers: ITableHeader[], row: Row, worksheet: Worksheet) {
  // 当前列的索引
  let colIndex = 1;
  headers.forEach((header) => {
    const { width, children } = header;
    if (children) {
      children.forEach((child) => {
        colIndex += 1;
      });
    } else {
      // 需要的列数，四舍五入
      const colNum = getColumnNumber(width);
      // 如果 colNum > 1 说明需要合并
      if (colNum > 1) {
        worksheet.mergeCells(Number(row.number), colIndex, Number(row.number), colIndex + colNum - 1);
      }
      colIndex += colNum;
    }
  });
}
// 设置列格式
const setColwrapText = (ws, col, length) => {
  for (let i = 0; i++; i < length) {
    ws.getCell(`${col}${i}`).alignment = { wrapText: true };
  }
};

const PREVIEW_COL_INDEX = 4;
const columns = [
  {
    title: '文件名称',
    dataIndex: 'fileName',
    key: 'fileName',
    with: 505,
  },
  {
    title: '问题位置',
    dataIndex: 'assetPositionTip',
    key: 'assetPositionTip',
    with: 150,
  },
  {
    title: '反馈人/记录人',
    dataIndex: 'creatorName',
    key: 'creatorName',
  },
  {
    title: '反馈时间',
    dataIndex: 'updateTime',
    key: 'updateTime',
  },
  {
    title: '反馈内容',
    dataIndex: 'content',
    key: 'content',
    width: 2000,
  },
];

columns.splice(PREVIEW_COL_INDEX, 0, {
  title: '反馈图片',
  dataIndex: 'preview',
  key: 'preview',
  // +50 兼容某些软件情况下的宽度设置
  width: PREVIEW_COL_WIDTH + ABUNDANT,
});

const readerPromise = (blob) => {
  return new Promise(function (resolve, reject) {
    const reader = new FileReader();
    reader.readAsDataURL(blob);
    reader.onloadend = function () {
      const base64data = (reader.result as string).replace('application/octet-stream', 'image/png');
      resolve(base64data);
    };
    reader.onerror = reject;
  });
};

export const fetchCacheImg = async (preview) => {
  try {
    const res = await fetch(preview, {
      mode: 'cors',
      // credentials: 'include',// 外部链接不可用
      cache: 'default',
    });
    return res.blob();
  } catch {
    return Promise.reject();
  }
};
const convertImgSize = (base64) => {
  return new Promise<{ width; height }>((resolve, reject) => {
    const image = new Image();
    image.onload = () => {
      const { width: originWidth, height: originHeight } = image;
      const imgScale = originWidth / originHeight;
      // const isFullHeight = PREVIEW_COL_WIDTH / DEFAULT_ROW_HEIGHT > imgScale;
      // const size = isFullHeight
      //   ? { width: imgScale * DEFAULT_ROW_HEIGHT, height: DEFAULT_ROW_HEIGHT }
      //   : { width: PREVIEW_COL_WIDTH, height: PREVIEW_COL_WIDTH / imgScale };
      const size = { width: PREVIEW_COL_WIDTH_PIXEL, height: PREVIEW_COL_WIDTH_PIXEL / imgScale };
      resolve(size);
    };
    image.onerror = reject;
    image.src = base64;
  });
};
const parseRichText = (htmlText, workbook, worksheet, startRowIndex) => {
  const parser = new DOMParser();
  // 将 HTML 文本解析为 DOM 文档
  const doc = parser.parseFromString(htmlText, 'text/html');
  // 创建一个 TreeWalker 对象，从根节点开始遍历文档
  const walker = document.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT, null);
  // 存储识别的文本内容和图片元素的 URL
  const textDoms: any[] = [];
  // 遍历 DOM 树
  let node;
  let imgs: any[] = [];
  let cellIndex = 0;
  while ((node = walker.nextNode())) {
    if (node.nodeType === Node.TEXT_NODE) {
      cellIndex = cellIndex + 1;
      const textContent = node.textContent.trim();
      if (textContent !== '') {
        textDoms.push({
          text: textContent,
        });
        textDoms.push({
          text: `\n`,
        });
      }
    } else if (node.nodeType === Node.ELEMENT_NODE && node.tagName === 'IMG') {
      const url = node.getAttribute('src');
      if (url) {
        for (let i = 1; i < IMG_ROW_COUNTS; i++) {
          textDoms.push({
            text: `\n`,
          });
        }
        imgs.push({
          text: `图片(${url})`,
          hyperlink: url,
          imgCellIndex: cellIndex,
        });
        cellIndex = cellIndex + IMG_ROW_COUNTS;
      }
    }
  }
  const row = worksheet.getRow(startRowIndex);
  // 计算高度+合并单元格
  const rowHeight = max([ROW_HEIGHT * cellIndex, PREVIEW_COL_HEIGHT]) || 0 + ABUNDANT; // 换算单元格的字符单位与图片的像素单位
  const rowCounts = Math.ceil(rowHeight / MAX_ROW_HEIGHT);
  if (rowCounts > 1) {
    for (let i = 1; i < rowCounts; i++) {
      worksheet.insertRow(startRowIndex + i, []);
      const row = worksheet.getRow(startRowIndex + i);
      row.height = MAX_ROW_HEIGHT;
    }
    mergeRows(worksheet, startRowIndex, startRowIndex + rowCounts - 1);
  }
  row.height = rowCounts > 1 ? MAX_ROW_HEIGHT : rowHeight;
  return { rowCounts, startRowIndex, textDoms, imgs, rowHeight, unitsCounts: cellIndex };
};

const insertRichTextImgs = async (imgs, unitsCounts, rowHeight, workbook, worksheet, rowIndex, rowCounts) => {
  await Promise.allSettled(
    imgs.map(async (content) => {
      const { hyperlink, text, imgCellIndex } = content;
      if (!hyperlink) {
        return Promise.resolve();
      }
      const blob = await fetchCacheImg(hyperlink);
      const base64data = (await readerPromise(blob)) as string;
      const imageId = workbook.addImage({
        base64: base64data,
        extension: 'png',
      });
      const { width, height } = await convertImgSize(base64data);
      const imgWidth = (width / height) * IMG_HEIGHT;
      const centerY =
        rowCounts > 1
          ? rowIndex - 1 + ((imgCellIndex + 1) * ROW_HEIGHT) / MAX_ROW_HEIGHT
          : rowIndex - 1 + ((imgCellIndex + 1) * ROW_HEIGHT) / ROW_HEIGHT_PIXEL / rowHeight;
      // console.log({ rowIndex, centerY, rowCounts, rowHeight, imgCellIndex });

      worksheet.addImage(imageId, {
        tl: { col: 5, row: centerY },
        ext: {
          width: imgWidth,
          height: IMG_HEIGHT,
        },
        editAs: 'oneCell',
      });
      return Promise.resolve();
    }),
  );
};

export const parseRichTextContext = async (workbook, worksheet, list = []) => {
  const rows: any[] = [];
  list?.reduce(
    (pre, current) => {
      const { content, type } = current;
      const isCheckResult = type === CommentSourceType.CheckResult;
      const start = pre.startRowIndex + pre.rowCounts;
      const rowUnit = isCheckResult
        ? {
            rowCounts: 1,
            skip: true,
            startRowIndex: start,
            textDoms: [],
            imgs: [],
            data: current,
          }
        : { ...parseRichText(content, workbook, worksheet, start), data: current };
      rows.push(rowUnit);
      return rowUnit;
    },
    { startRowIndex: 3, rowCounts: 0 },
  );

  // console.log(rows);
  await insertPreview(workbook, worksheet, rows);

  await Promise.allSettled(
    rows.map(async ({ skip, textDoms, startRowIndex, rowHeight, imgs, rowCounts, unitsCounts }) => {
      if (skip || !textDoms?.length) {
        return Promise.resolve();
      } else {
        await insertRichTextImgs(imgs, unitsCounts, rowHeight, workbook, worksheet, startRowIndex, rowCounts);
        worksheet.getCell(`F${startRowIndex}`).value = { richText: textDoms };
        return Promise.resolve();
      }
    }),
  );
};
export const insertPreview = async (workbook, worksheet, list) => {
  await Promise.allSettled(
    list.map(async ({ data, startRowIndex }) => {
      const { preview } = data;
      // console.time(`${index}-fetchImg`);
      const blob = await fetchCacheImg(preview.url); // 0.7.1.1修改数据结构
      // console.timeEnd(`${index}-fetchImg`);
      // console.time(`${index}-baseImg`);
      const base64data = (await readerPromise(blob)) as string;
      const imageId = workbook.addImage({
        base64: base64data,
        extension: 'png',
      });
      // console.timeEnd(`${index}-baseImg`);
      // console.time(`${index}-addImg`);
      const { width: imgWidth, height: imgHeight } = await convertImgSize(base64data);
      const isFullHeight = PREVIEW_COL_HEIGHT / PREVIEW_COL_WIDTH < imgHeight / imgWidth;
      const width = isFullHeight ? (PREVIEW_COL_HEIGHT * imgWidth) / imgHeight : PREVIEW_COL_WIDTH;
      const height = isFullHeight ? PREVIEW_COL_HEIGHT : (PREVIEW_COL_WIDTH * imgHeight) / imgWidth;
      worksheet.addImage(imageId, {
        tl: { col: PREVIEW_COL_INDEX, row: startRowIndex - 1 },
        ext: { width, height },
        editAs: 'oneCell',
      });
      const row = worksheet.getRow(startRowIndex);
      // 换算单元格的字符单位与图片的像素单位
      row.height = PREVIEW_COL_HEIGHT + ABUNDANT;
      // console.log({ height });
      // console.timeEnd(`${index}-addImg`);
    }),
  );
};
export const onExportBasicExcel = async (taskSubject, stage, searchVersion, list, onDone) => {
  // 创建工作簿
  const fileName = `${taskSubject}-${stage}-${searchVersion}`;
  const exceljs = await import('@fuxi/exceljs');
  const workbook = new exceljs.Workbook();
  // 添加sheet
  const worksheet = workbook.addWorksheet('sheet 1');
  worksheet.mergeCells('A2:L2');
  // ...合并的单元格被链接起来了
  worksheet.getCell('A2').value = `说明：1、暂不支持导出反馈附件，若反馈存在附件，请自行进行附件下载操作`;
  // worksheet.addRow
  // 设置 sheet 的默认行高
  worksheet.properties.defaultRowHeight = DEFAULT_ROW_HEIGHT;
  // 设置列
  worksheet.columns = generateHeaders(columns);
  // 添加行
  const rows = handleExcelData(list);
  worksheet.addRows(rows);

  await parseRichTextContext(workbook, worksheet, list);

  // 设置单元格
  worksheet.eachRow((row, rowNumber) => {
    row.eachCell((cell, colNumber) => {
      cell.alignment = {
        vertical: 'top',
        horizontal: 'left',
        wrapText: true,
      };
    });
  });
  // 启用列筛选
  worksheet.autoFilter = 'A1:G1';
  // 导出excel
  saveWorkbook(workbook, `${fileName}.xlsx`);
  onDone();
};
